import { useRef, useCallback, useImperativeHandle, forwardRef, useState, useEffect } from "react";
import Editor, { type OnMount, type BeforeMount } from "@monaco-editor/react";
import type { editor as monacoEditor, IDisposable } from "monaco-editor";

export interface CompletionItem {
  label: string;
  kind: "table" | "column" | "function" | "view" | "keyword";
}

interface SqlEditorProps {
  value: string;
  onChange: (value: string) => void;
  onRun: () => void;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  completionItems?: CompletionItem[];
}

const SQL_KEYWORDS = [
  "SELECT", "FROM", "WHERE", "INSERT", "UPDATE", "DELETE", "CREATE", "ALTER",
  "DROP", "JOIN", "LEFT", "RIGHT", "INNER", "OUTER", "ON", "AND", "OR", "NOT",
  "IN", "EXISTS", "BETWEEN", "LIKE", "ORDER", "BY", "GROUP", "HAVING", "LIMIT",
  "OFFSET", "AS", "DISTINCT", "UNION", "INTERSECT", "EXCEPT", "CASE", "WHEN",
  "THEN", "ELSE", "END", "NULL", "TRUE", "FALSE", "IS", "SET", "VALUES", "INTO",
  "TABLE", "INDEX", "VIEW", "FUNCTION", "TRIGGER", "CONSTRAINT", "PRIMARY",
  "FOREIGN", "KEY", "REFERENCES", "CASCADE", "DEFAULT", "CHECK", "UNIQUE",
  "BEGIN", "COMMIT", "ROLLBACK",
];

export interface SqlEditorHandle {
  insertAtCursor: (text: string) => void;
  formatSql: () => Promise<string>;
}

// Hardcoded hex themes derived from theme.css OKLCh values.
// Monaco requires #RRGGBB(AA); it cannot parse oklch() or CSS variables.
const LIGHT_THEME = {
  base: "vs" as const,
  inherit: true,
  rules: [
    { token: "keyword", foreground: "2563eb" },    // lapis blue
    { token: "string", foreground: "16a34a" },     // green
    { token: "string.sql", foreground: "16a34a" },
    { token: "number", foreground: "d97706" },     // amber
    { token: "comment", foreground: "94a3b8", fontStyle: "italic" },
    { token: "operator", foreground: "475569" },   // slate
    { token: "operator.sql", foreground: "475569" },
    { token: "type", foreground: "7c3aed" },       // violet
    { token: "predefined", foreground: "2563eb" },  // lapis
    { token: "identifier", foreground: "0f172a" },  // foreground
  ],
  colors: {
    "editor.background": "#ffffff",          // --card
    "editor.foreground": "#0f172a",          // --foreground
    "editor.lineHighlightBackground": "#e8ecf4", // --secondary
    "editor.selectionBackground": "#2563eb20",
    "editorCursor.foreground": "#2563eb",    // --primary
    "editorLineNumber.foreground": "#64748b", // --muted-foreground
    "editorLineNumber.activeForeground": "#0f172a", // --foreground
    "editor.inactiveSelectionBackground": "#2563eb10",
    "editorWidget.background": "#ffffff",    // --popover
    "editorWidget.border": "#cbd5e1",        // --border
    "editorSuggestWidget.background": "#ffffff",
    "editorSuggestWidget.border": "#cbd5e1",
    "editorSuggestWidget.selectedBackground": "#e8ecf4", // --accent
    "editorGutter.background": "#ffffff",
    "input.background": "#ffffff",
    "input.border": "#cbd5e1",
    "focusBorder": "#2563eb",
    "editorBracketMatch.background": "#2563eb20",
    "editorBracketMatch.border": "#2563eb40",
  },
};

const DARK_THEME = {
  base: "vs-dark" as const,
  inherit: true,
  rules: [
    { token: "keyword", foreground: "c678dd" },    // purple
    { token: "string", foreground: "7ec97f" },     // green
    { token: "string.sql", foreground: "7ec97f" },
    { token: "number", foreground: "d19a66" },     // orange
    { token: "comment", foreground: "5a6a7a", fontStyle: "italic" },
    { token: "operator", foreground: "4fc1e9" },   // teal
    { token: "operator.sql", foreground: "4fc1e9" },
    { token: "type", foreground: "e5c07b" },       // yellow
    { token: "predefined", foreground: "4fc1e9" },  // teal
    { token: "identifier", foreground: "d4dae3" },  // foreground
  ],
  colors: {
    "editor.background": "#142130",          // --card
    "editor.foreground": "#d4dae3",          // --foreground
    "editor.lineHighlightBackground": "#1a2a3a", // --secondary
    "editor.selectionBackground": "#253545",
    "editorCursor.foreground": "#4fc1e9",    // --primary
    "editorLineNumber.foreground": "#5a6a7a", // --muted-foreground
    "editorLineNumber.activeForeground": "#d4dae3", // --foreground
    "editor.inactiveSelectionBackground": "#25354533",
    "editorWidget.background": "#182636",    // --popover
    "editorWidget.border": "#253545",        // --border
    "editorSuggestWidget.background": "#182636",
    "editorSuggestWidget.border": "#253545",
    "editorSuggestWidget.selectedBackground": "#1a2a3a",
    "editorGutter.background": "#142130",
    "input.background": "#142130",
    "input.border": "#253545",
    "focusBorder": "#4fc1e9",
    "editorBracketMatch.background": "#4fc1e920",
    "editorBracketMatch.border": "#4fc1e940",
  },
};

function defineLinabaseThemes(monaco: Parameters<BeforeMount>[0]) {
  monaco.editor.defineTheme("linabase-light", LIGHT_THEME);
  monaco.editor.defineTheme("linabase-dark", DARK_THEME);
}

export const SqlEditorMonaco = forwardRef<SqlEditorHandle, SqlEditorProps>(function SqlEditorMonaco(
  {
    value,
    onChange,
    onRun,
    placeholder: _placeholder = "SELECT * FROM your_table LIMIT 50;",
    minHeight = "200px",
    maxHeight,
    completionItems,
  },
  ref,
) {
  const editorRef = useRef<monacoEditor.IStandaloneCodeEditor | null>(null);
  const onRunRef = useRef(onRun);
  const completionItemsRef = useRef(completionItems);

  useEffect(() => {
    onRunRef.current = onRun;
  }, [onRun]);

  useEffect(() => {
    completionItemsRef.current = completionItems;
  }, [completionItems]);
  const completionDisposableRef = useRef<IDisposable | null>(null);

  // Track dark mode for theme switching
  const [dark, setDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Dispose completion provider on unmount
  useEffect(() => {
    return () => {
      completionDisposableRef.current?.dispose();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    insertAtCursor(text: string) {
      const editor = editorRef.current;
      if (!editor) return;
      const selection = editor.getSelection();
      if (!selection) return;
      const model = editor.getModel();
      if (!model) return;
      // Add leading space if needed
      const pos = selection.getStartPosition();
      const charBefore = pos.column > 1
        ? model.getValueInRange({
            startLineNumber: pos.lineNumber,
            startColumn: pos.column - 1,
            endLineNumber: pos.lineNumber,
            endColumn: pos.column,
          })
        : "";
      const needsSpace = charBefore !== "" && charBefore !== " " && charBefore !== "\n" && charBefore !== "\t";
      const insert = (needsSpace ? " " : "") + text;

      editor.executeEdits("insertAtCursor", [
        { range: selection, text: insert },
      ]);
      editor.focus();
    },
    async formatSql() {
      const editor = editorRef.current;
      if (!editor) return "";
      const currentValue = editor.getValue();
      if (!currentValue.trim()) return currentValue;
      const { format } = await import("sql-formatter");
      const formatted = format(currentValue, {
        language: "postgresql",
        tabWidth: 2,
        keywordCase: "upper",
        logicalOperatorNewline: "before",
      });
      editor.setValue(formatted);
      return formatted;
    },
  }));

  const handleBeforeMount: BeforeMount = useCallback((monaco) => {
    defineLinabaseThemes(monaco);
  }, []);

  const [editorHeight, setEditorHeight] = useState(minHeight);

  const updateHeight = useCallback((editor: monacoEditor.IStandaloneCodeEditor) => {
    const contentHeight = editor.getContentHeight();
    const minPx = parseInt(minHeight, 10) || 80;
    // Parse maxHeight (e.g. "60vh" -> px)
    let maxPx = Infinity;
    if (maxHeight) {
      if (maxHeight.endsWith("vh")) {
        maxPx = (parseFloat(maxHeight) / 100) * window.innerHeight;
      } else {
        maxPx = parseInt(maxHeight, 10) || Infinity;
      }
    }
    const h = Math.max(minPx, Math.min(contentHeight, maxPx));
    setEditorHeight(`${h}px`);
  }, [minHeight, maxHeight]);

  const handleMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    // Cmd/Ctrl+Enter to run query
    editor.addAction({
      id: "run-query",
      label: "Run Query",
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter],
      run: () => onRunRef.current(),
    });

    // Cmd/Ctrl+Shift+F to format SQL
    editor.addAction({
      id: "format-sql",
      label: "Format SQL",
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF],
      run: async () => {
        const currentValue = editor.getValue();
        if (!currentValue.trim()) return;
        const { format } = await import("sql-formatter");
        const formatted = format(currentValue, {
          language: "postgresql",
          tabWidth: 2,
          keywordCase: "upper",
          logicalOperatorNewline: "before",
        });
        editor.setValue(formatted);
      },
    });

    // Disable default minimap, scrollbar beyond last line, etc. for compact feel
    editor.updateOptions({
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      overviewRulerLanes: 0,
      hideCursorInOverviewRuler: true,
      overviewRulerBorder: false,
      renderLineHighlight: "line",
      lineNumbersMinChars: 3,
      glyphMargin: false,
      folding: false,
      fontSize: 12,
      lineHeight: 21,
      fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
      padding: { top: 12, bottom: 12 },
      scrollbar: {
        vertical: "auto",
        horizontal: "auto",
        verticalScrollbarSize: 8,
        horizontalScrollbarSize: 8,
      },
      wordWrap: "on",
      automaticLayout: true,
    });

    // Auto-resize to content
    editor.onDidContentSizeChange(() => updateHeight(editor));
    updateHeight(editor);

    // Register SQL completion provider
    const kindMap: Record<CompletionItem["kind"], number> = {
      table: monaco.languages.CompletionItemKind.Module,
      column: monaco.languages.CompletionItemKind.Field,
      function: monaco.languages.CompletionItemKind.Function,
      view: monaco.languages.CompletionItemKind.Interface,
      keyword: monaco.languages.CompletionItemKind.Keyword,
    };

    completionDisposableRef.current?.dispose();
    completionDisposableRef.current = monaco.languages.registerCompletionItemProvider("pgsql", {
      provideCompletionItems(model: any, position: any) {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const suggestions: Array<{
          label: string;
          kind: number;
          insertText: string;
          range: typeof range;
          sortText?: string;
        }> = [];

        // Add keyword suggestions
        for (const kw of SQL_KEYWORDS) {
          suggestions.push({
            label: kw,
            kind: kindMap.keyword,
            insertText: kw,
            range,
            sortText: `2_${kw}`,
          });
        }

        // Add user-provided completion items (tables, columns, views, functions)
        const items = completionItemsRef.current;
        if (items) {
          for (const item of items) {
            suggestions.push({
              label: item.label,
              kind: kindMap[item.kind],
              insertText: item.label,
              range,
              sortText: `1_${item.label}`,
            });
          }
        }

        return { suggestions };
      },
    });
  }, [updateHeight]);

  return (
    <div
      style={{ minHeight, maxHeight }}
      className="[&_.monaco-editor]:outline-none [&_.monaco-editor_.overflow-guard]:rounded-none"
    >
      <Editor
        language="pgsql"
        theme={dark ? "linabase-dark" : "linabase-light"}
        value={value}
        onChange={(v) => onChange(v ?? "")}
        beforeMount={handleBeforeMount}
        onMount={handleMount}
        options={{
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 13,
          lineHeight: 21,
          fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
          wordWrap: "on",
          automaticLayout: true,
          padding: { top: 12, bottom: 12 },
          lineNumbersMinChars: 3,
          glyphMargin: false,
          folding: false,
          overviewRulerLanes: 0,
          renderLineHighlight: "line",
          scrollbar: {
            vertical: "auto",
            horizontal: "auto",
            verticalScrollbarSize: 8,
            horizontalScrollbarSize: 8,
          },
        }}
        height={editorHeight}
      />
    </div>
  );
});
